import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  onAuthStateChanged,
  signOut
} from 'firebase/auth';

import {
  doc,
  getDoc
} from 'firebase/firestore';

import {
  auth,
  db
} from '../firebase/firebase';

import {
  resolvePermissions
} from '../permissions/roles';

const AuthContext = createContext(null);

const EMPTY = {
  user: null,
  profile: null,
  permissions: new Set(),
  loading: false,
  authError: ''
};

const ADMIN_ROLES = new Set([
  'gerant',
  'directeur',
  'inspecteur',
  'agent_ecole',
  'agent_college',
  'associe'
]);

export const normalizeValue = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

function normalizedRoles(profile) {
  const raw =
    profile?.adminRoles ??
    profile?.adminRole ??
    profile?.role ??
    [];

  return (Array.isArray(raw) ? raw : [raw])
    .map(normalizeValue)
    .map((role) =>
      role === 'admin' ||
      role === 'administration'
        ? 'gerant'
        : role
    )
    .filter(Boolean);
}

function normalizedAccountType(profile) {
  const explicit =
    normalizeValue(profile?.accountType);

  if (explicit) {
    if (explicit === 'enseignant') {
      return 'teacher';
    }

    return explicit;
  }

  const role =
    normalizeValue(profile?.role);

  if (role === 'parent') {
    return 'parent';
  }

  if (
    role === 'teacher' ||
    role === 'enseignant'
  ) {
    return 'teacher';
  }

  if (
    role === 'administration' ||
    role === 'admin' ||
    ADMIN_ROLES.has(role)
  ) {
    return 'administration';
  }

  return '';
}

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    ...EMPTY,
    loading: true
  });

  useEffect(() => {
    const unsubscribe =
      onAuthStateChanged(
        auth,
        async (user) => {
          if (!user) {
            setState(EMPTY);
            return;
          }

          setState((current) => ({
            ...current,
            loading: true,
            authError: ''
          }));

          try {
            const snapshot =
              await getDoc(
                doc(
                  db,
                  'users',
                  user.uid
                )
              );

            if (!snapshot.exists()) {
              throw new Error(
                'Profil utilisateur introuvable.'
              );
            }

            const profile = {
              id: snapshot.id,
              ...snapshot.data()
            };

            const accountType =
              normalizedAccountType(profile);

            const roles =
              normalizedRoles(profile);

            const approved =
              normalizeValue(
                profile.status
              ) === 'approved';

            const allowedAdmin =
              accountType ===
                'administration' &&
              roles.some((role) =>
                ADMIN_ROLES.has(role)
              );

            const allowed =
              approved &&
              (
                accountType ===
                  'parent' ||
                accountType ===
                  'teacher' ||
                allowedAdmin
              );

            if (!allowed) {
              await signOut(auth);

              setState({
                ...EMPTY,
                authError:
                  'Ce compte n’est pas autorisé ou n’est pas encore approuvé.'
              });

              return;
            }

            setState({
              user,
              profile,

              // Seuls les comptes administration
              // utilisent les permissions administratives.
              permissions:
                accountType ===
                'administration'
                  ? resolvePermissions(
                      profile
                    )
                  : new Set(),

              loading: false,
              authError: ''
            });
          } catch (error) {
            console.error(
              'Chargement du profil :',
              error
            );

            await signOut(auth)
              .catch(() => {});

            setState({
              ...EMPTY,
              authError:
                'Impossible de charger votre profil.'
            });
          }
        }
      );

    return unsubscribe;
  }, []);

  const value = useMemo(() => {
    const accountType =
      normalizedAccountType(
        state.profile
      );

    const roles =
      normalizedRoles(
        state.profile
      );

    const refreshProfile = async () => {
      if (!state.user?.uid) return null;

      const snapshot = await getDoc(
        doc(db, 'users', state.user.uid)
      );

      if (!snapshot.exists()) {
        throw new Error('Profil utilisateur introuvable.');
      }

      const nextProfile = {
        id: snapshot.id,
        ...snapshot.data()
      };

      setState((current) => ({
        ...current,
        profile: nextProfile,
        loading: false,
        authError: ''
      }));

      return nextProfile;
    };

    return {
      ...state,

      accountType,
      roles,
      refreshProfile,

      isParent:
        accountType === 'parent',

      isTeacher:
        accountType === 'teacher',

      isAdmin:
        accountType ===
        'administration',

      isGerant:
        accountType ===
          'administration' &&
        roles.includes('gerant'),

      can: (permission) =>
        state.permissions.has(
          permission
        ),

      logout: () =>
        signOut(auth)
    };
  }, [state]);

  return (
    <AuthContext.Provider
      value={value}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context =
    useContext(AuthContext);

  if (!context) {
    throw new Error(
      'useAuth doit être utilisé dans AuthProvider.'
    );
  }

  return context;
}